import * as queryString from 'querystring';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Tooltip } from '@material-ui/core';

import { ApiParams } from '../../../common/api/api';
import TaskStatusDot from '../../../common/TaskStatusDot';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import {
  RenderColumnMenu,
  RenderOptions
} from '../../../common/trace-board/interfaces/TraceBoardColumn';
import TraceBoard from '../../../common/trace-board/TraceBoard';
import { TraceBoardContextState } from '../../../common/trace-board/TraceBoardContext';
import { Task } from '../../tasks/Task.interface';
import { useWorkflowContext } from '../../workflow/Workflow.context';
import { getStateNodeByStatus } from '../../workflow/Workflow.hooks';
import {
  Hazard,
  HazardAction,
  HazardBoardData,
  HazardCause,
  HazardControl
} from '../Hazard.interface';
import styled from 'styled-components';
import { TraceBoardItem } from '../../../common/trace-board/interfaces/TraceBoardItem';
import { Person, WarningRounded } from '@material-ui/icons';
import {
  useCreateHazardAction,
  useCreateHazardCause,
  useCreateHazardControl,
  useGetHazardBoard,
  useGetHazardBoardReferences,
  useUpdateHazardAction,
  useUpdateHazardCause,
  useUpdateHazardControl
} from '../Hazard.hooks';
import BoardColumnMenu from '../../tasks/board/BoardColumnMenu';

const StyledSubContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px 12px;
  .project-reference {
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    border: 1px solid #fec400;
    color: #b18d13;
    background: #fdf7eb;
    padding: 3px 5px;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  .assigned {
    display: flex;
    align-items: center;
    .icon-wrapper {
      display: flex;
      margin-right: 10px;
      height: 24px;
      width: 24px;
      border-radius: 12px;
      align-items: center;
      justify-content: center;
      background: #efefef;
      overflow: hidden;
      .icon {
        width: 18px;
        height: 18px;
      }
    }
  }
  .view-details {
    display: flex;
    flex-direction: column;
    .button {
      margin-top: 8px;
    }
  }
`;

const StyledWarning = styled(WarningRounded)`
  color: #f47560;
  width: 20px;
  height: 20px;
  margin-right: 4px;
`;
export interface HazardBoardTraceProp {
  hazard: Hazard;
  productId: number;
  apiParams?: ApiParams;
}

const HazardBoardTrace: React.FC<HazardBoardTraceProp> = ({
  hazard,
  productId,
  apiParams
}) => {
  const { getWorkflow } = useWorkflowContext();
  const traceBoardRef = useRef<TraceBoardContextState>(null!);
  const apiParamsRef = useRef(JSON.stringify(apiParams));
  const [hazardBoards, setHazardBoards] = useState<HazardBoardData[]>([]);
  const hasFilters = Object.keys(apiParams || {}).length > 0;

  const {
    createdHazardCause,
    isCreatingHazardCause,
    createHazardCauseError,
    createHazardCause
  } = useCreateHazardCause(hazard.id);

  const { updatedHazardCause, updateHazardCause } = useUpdateHazardCause(
    hazard.id
  );
  const {
    createdHazardControl,
    isCreatingHazardControl,
    createHazardControlError,
    createHazardControl
  } = useCreateHazardControl(hazard.id);

  const { updatedHazardControl, updateHazardControl } = useUpdateHazardControl(
    hazard.id
  );
  const {
    createdHazardAction,
    isCreatingHazardAction,
    createHazardActionError,
    createHazardAction
  } = useCreateHazardAction(hazard.id);

  const { updatedHazardAction, updateHazardAction } = useUpdateHazardAction(
    hazard.id
  );

  const {
    hazardBoard: loadedHazardBoard,
    isLoading,
    getHazardBoard
  } = useGetHazardBoard(hazard.id);
  useEffect(() => {
    if (loadedHazardBoard) {
      // remove the previous product board and add the newly loaded one
      setHazardBoards(hazardBoards => {
        hazardBoards = hazardBoards.filter(p => Boolean(p.referencedHazardId));
        return [...hazardBoards, loadedHazardBoard];
      });
    }
  }, [loadedHazardBoard]);
  useEffect(() => {
    getHazardBoard(apiParams);
    traceBoardRef.current.setLoadingItem(undefined);
  }, [
    apiParams,
    createdHazardCause,
    updatedHazardCause,
    createdHazardControl,
    updatedHazardControl,
    createdHazardAction,
    updatedHazardAction,
    getHazardBoard
  ]);
  const {
    hazardBoardReferences: loadedHazardReferences,
    getHazardBoardReferences
  } = useGetHazardBoardReferences();
  useEffect(() => {
    if (loadedHazardReferences) {
      // prevent duplicates
      setHazardBoards(hazardBoards => [
        ...hazardBoards.filter(
          p =>
            !loadedHazardReferences.some(
              r =>
                r.hazardId === p.hazardId &&
                r.referencedHazardId === p.referencedHazardId
            )
        ),
        ...loadedHazardReferences
      ]);
    }
  }, [loadedHazardReferences]);

  const renderColumnMenu = (
    { columnIndex, column }: RenderColumnMenu,
    onClose?: () => void,
    onRender?: () => void
  ) => {
    const taskEntities = column.items.map(item => item.data);
    return (
      <BoardColumnMenu
        taskEntities={taskEntities}
        onRender={onRender}
        onChange={() => {
          onClose?.();
          getHazardBoard(apiParams);
          traceBoardRef.current.setLoadingItem(undefined);
        }}
      />
    );
  };

  const countLinkedItemsRequiresReview = (
    item: TraceBoardItem,
    columnIndex: number
  ) => {
    return (
      item.connectors
        ?.filter(c => c.columnIndex === columnIndex - 1)
        .reduce((count, connector) => {
          const task: Task = connector.sourceItem.data.task;
          const stateNode = getWorkflowState(task);
          if (stateNode?.type !== 'final') {
            count++;
          }
          count += countLinkedItemsRequiresReview(
            connector.sourceItem,
            connector.columnIndex
          );
          return count;
        }, 0) || 0
    );
  };

  const renderTitleIcon = ({
    item,
    isSelected,
    isCollapsed
  }: RenderOptions) => {
    if (!item.data?.task) return null;
    const task: Task = item.data.task;
    if (!isCollapsed) return null;
    return <TaskStatusDot task={task} />;
  };
  const renderTitleAccessory = ({
    columnIndex,
    item,
    isCollapsed
  }: RenderOptions) => {
    if (!item.data?.task) return null;
    const task: Task = item.data.task;
    if (isCollapsed) return null;
    // find an item in the row that is not in the final state
    const stateNode = getWorkflowState(task);
    const requiresReviewCount =
      stateNode?.type === 'final'
        ? countLinkedItemsRequiresReview(item, columnIndex)
        : 0;
    return (
      <>
        {requiresReviewCount ? (
          <Tooltip
            title={`${requiresReviewCount} upstream linked item(s) that requires review`}
            placement='top'
            arrow
          >
            <StyledWarning />
          </Tooltip>
        ) : null}
        <TaskStatusLabel size='small' task={task} />
      </>
    );
  };
  const renderSubContent = ({
    item,
    column,
    columnIndex,
    isSelected,
    isCollapsed
  }: RenderOptions) => {
    let path = '';
    if (!item.data) return null;
    const hazardId = item.data.hazardId;
    switch (columnIndex) {
      case 0:
        path = `/portal/products/${productId}/hazards/${hazardId}/causes/${item.id}?from=trace`;
        break;
      case 1:
        path = `/portal/products/${productId}/hazards/${hazardId}/controls/${item.id}?from=trace`;
        break;
      case 2:
        path = `/portal/products/${productId}/hazards/${hazardId}/actions/${item.id}?from=trace`;
        break;
    }
    if (isCollapsed) return null;
    const task = item.data?.task as Task;
    return (
      <StyledSubContent>
        {hazardId !== hazard.id && (
          <div className='project-reference'>
            {
              hazardBoards.find(h => h.referencedHazardId === hazardId)
                ?.referencedHazardTitle
            }
          </div>
        )}
        {task?.user && (
          <div className='assigned'>
            <div className='icon-wrapper'>
              <Person className='icon' />
            </div>
            {task.user.firstName} {task.user.lastName}
          </div>
        )}
        <div className={`view-details${isSelected ? '' : ' hover-only'}`}>
          <Button
            variant='outlined'
            color='primary'
            className='button'
            component={Link}
            to={path}
          >
            View Details
          </Button>
        </div>
      </StyledSubContent>
    );
  };
  const { columns, lookupReferencedHazards } = useMemo(() => {
    let lookupReferencedHazards: {
      hazardId: number;
      referencedHazardId: number;
    }[] = [];
    const query = `from=trace${
      hasFilters ? `&${queryString.encode(apiParams || {})}` : ''
    }`;
    // wait till hazardBoards is finished loading to prevent display of
    // empty state message
    if (isLoading || hazardBoards?.length === 0)
      return { columns: [], lookupReferencedHazards: [] };
    const columns = [
      {
        title: 'Causes',
        headerLink: `./causes?${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: hazardBoards.reduce((results, hazardBoard) => {
          const causes =
            hazardBoard?.causes.map(cause => {
              lookupReferencedHazards.push(
                ...(cause.controls
                  ?.filter(d => d.hazardId !== cause.hazardId)
                  ?.map(d => ({
                    hazardId: cause.hazardId,
                    referencedHazardId: d.hazardId
                  })) || [])
              );
              return {
                id: cause.id,
                title: `CAUSE-${cause.id}`,
                description: cause.title,
                externalReference: cause.hazardId !== hazard.id,
                data: cause
              };
            }) || [];
          results.push(...causes);
          return results;
        }, [] as TraceBoardItem[])
      },
      {
        title: 'Controls',
        headerLink: `./controls?${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: hazardBoards.reduce((results, hazardBoard) => {
          const controls =
            hazardBoard?.controls.map(control => {
              lookupReferencedHazards.push(
                ...(control.actions
                  ?.filter(d => d.hazardId !== control.hazardId)
                  ?.map(d => ({
                    hazardId: control.hazardId,
                    referencedHazardId: d.hazardId
                  })) || [])
              );
              return {
                id: control.id,
                title: `CONTROL-${control.id}`,
                description: control.title,
                externalReference: control.hazardId !== hazard.id,
                data: control
              };
            }) || [];
          results.push(...controls);
          return results;
        }, [] as TraceBoardItem[])
      },
      {
        title: 'Actions',
        headerLink: `./actions?${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items: hazardBoards.reduce((results, hazardBoard) => {
          const actions =
            hazardBoard?.actions.map(action => ({
              id: action.id,
              title: `ACTION-${action.id}`,
              description: action.title,
              externalReference: action.hazardId !== hazard.id,
              data: action
            })) || [];
          results.push(...actions);
          return results;
        }, [] as TraceBoardItem[])
      }
    ];
    // dedupe reference products
    lookupReferencedHazards = lookupReferencedHazards
      .filter(
        ({ hazardId, referencedHazardId }, index) =>
          lookupReferencedHazards.findIndex(
            r =>
              r.hazardId === hazardId &&
              r.referencedHazardId === referencedHazardId
          ) === index
      )
      // filter out any products already referenced (received from server)
      .filter(
        ({ hazardId, referencedHazardId }) =>
          !hazardBoards.some(
            r =>
              r.hazardId === hazardId &&
              r.referencedHazardId === referencedHazardId
          )
      );

    return { columns, lookupReferencedHazards };
  }, [hazardBoards]);

  // get all
  useEffect(() => {
    if (lookupReferencedHazards.length > 0) {
      const { hazardId } = lookupReferencedHazards[0];
      getHazardBoardReferences(
        hazardId,
        lookupReferencedHazards
          .filter(r => r.hazardId === hazardId)
          .map(r => r.referencedHazardId),
        apiParams
      );
    }
  }, [apiParams, getHazardBoardReferences, lookupReferencedHazards]);

  const getWorkflowState = (task: Task | undefined) => {
    return getStateNodeByStatus(task?.status, getWorkflow(task));
  };

  return (
    <TraceBoard
      innerRef={traceBoardRef}
      heading={hasFilters ? 'Filtered Cards' : undefined}
      orphanedRowHeading={hasFilters ? 'Orphaned Cards from filter' : undefined}
      columns={columns}
      isLoading={isLoading}
      filterColumnItems={(primaryItem, sourceItem, column, columnIndex) => {
        const data = sourceItem?.data;
        switch (columnIndex) {
          // controls
          case 1: {
            if (!data) return [];
            const cause = data as HazardCause;
            return column.items.filter(item =>
              cause.controls?.some(c => c.id === item.id)
            );
          }
          // actions
          case 2: {
            if (!data) return [];
            const control = data as HazardControl;
            return column.items.filter(item =>
              control.actions?.some(a => a.id === item.id)
            );
          }
          default:
            return [];
        }
      }}
      filterOrphanedItems={(column, columnIndex) => {
        const hazardBoard = hazardBoards.find(h => !h.referencedHazardId);
        if (!hazardBoard) return [];
        switch (columnIndex) {
          // controls
          case 1: {
            return column.items.filter(
              item =>
                (item.id === 'new' ||
                  item.data?.hazardId === hazardBoard.hazardId) &&
                !hazardBoard.causes.some(c =>
                  c.controls?.some(control => control.id === item.id)
                )
            );
          }
          // actions
          case 2: {
            return column.items.filter(item => {
              // only get items in current product board
              if (
                item.id !== 'new' &&
                item.data?.hazardId !== hazardBoard.hazardId
              ) {
                return false;
              }
              // get all related controls
              const controls = hazardBoard?.controls.filter(control =>
                control.actions?.some(action => action.id === item.id)
              );
              // see if any of the related controls does not have a cause
              return !hazardBoard?.causes.some(c =>
                c.controls?.some(cc => controls?.some(c => c.id === cc.id))
              );
            });
          }
          default:
            return [];
        }
      }}
      canEditItem={(sourceItem, column, columnIndex) => {
        const stateNode = getWorkflowState(sourceItem.data?.task);
        if (!stateNode) return;
        const allowedFields = stateNode.meta?.allowedFields || [];
        return allowedFields.includes('title');
      }}
      canLinkNewItem={(sourceItem, column, columnIndex) => {
        return columnIndex !== 2;
      }}
      onAddItem={(newItem, sourceItem, column, columnIndex) => {
        const sourceItemIds = sourceItem ? [sourceItem.id] : [];
        console.log(newItem);
        switch (columnIndex) {
          case 0:
            createHazardCause({ title: newItem.description });
            break;
          case 1:
            createHazardControl({
              title: newItem.description,
              causeIds: sourceItemIds
            });
            break;
          case 2:
            createHazardAction({
              title: newItem.description,
              controlIds: sourceItemIds
            });
            break;
        }
      }}
      onUpdateItem={(item, column, columnIndex) => {
        console.log(item);
        switch (columnIndex) {
          case 0:
            updateHazardCause({
              id: item.data.id,
              title: item.description
            });
            break;
          case 1:
            updateHazardControl({
              id: item.data.id,
              title: item.description
            });
            break;
          case 2:
            updateHazardAction({
              id: item.data.id,
              title: item.description
            });
            break;
        }
      }}
    />
  );
};

export default HazardBoardTrace;
